import { useEffect, useRef } from "react";
import { ersteZustandswahl } from "../../state/Speicher.tsx";
import type { BestandLaufErgebnis } from "../../state/Speicher.tsx";
import type { Nutzer } from "../../state/reduzierer.ts";
import type { BoardStateSelection } from "../../state/adresse.ts";
import type { AnsichtsZugriff } from "../../state/useAnsicht.ts";
import { normalisierteRolle } from "../Users/RollenAuswahl.tsx";
import type { AktiveSeite } from "./Navigation.tsx";

/** Schnelle Filterwechsel erzeugen nur den Teilabruf fuer die letzte Auswahl. */
const ZUSTANDSWECHSEL_ENTPRELLUNG_MS = 250;

/** Eine Filterauswahl: ihr Schluessel (zum Vergleichen) und die Auswahl selbst (zum Zurueckschreiben). */
interface Filter {
  schluessel: string;
  auswahl: BoardStateSelection;
}

function filterVon(auswahl: BoardStateSelection): Filter {
  return { schluessel: auswahl.join(","), auswahl };
}

/**
 * Synchronisiert den serverseitigen Bestandsfilter mit der Board-Auswahl. Der Erstabruf bleibt
 * in `Speicher.tsx`: derselbe `ersteZustandswahl`-Wert verhindert einen doppelten oder falschen
 * Erstabruf bei Archiv-Tieflinks. Die Synchronisierung muss auch ausserhalb des Boards erfolgen,
 * damit der spaetere Einstieg mit einem anderen Filter einen Nachladelauf ausloest.
 *
 * Andere Seiten setzen den Adress-Boardblock auf `["active"]` zurueck; das darf keinen Abruf
 * ausloesen. `payment_manager` braucht dagegen auf jeder Seite aktive UND archivierte
 * Bestellungen, damit offene Zahlungsaufgaben archivierter Bestellungen sichtbar bleiben.
 * Der kanonische Schluessel vergleicht Inhalte statt Array-Referenzen; auch die leere Auswahl (`""`)
 * ist ein gueltiger Filter.
 *
 * WELCHER FILTER GILT ALS GELADEN: die EINE Wahrheit ist `bestaetigterFilter` des Speichers (Fix-Runde 1) -
 * der Filter des zuletzt gelungenen Laufs, gleich wer ihn anstiess. Der Haken fuehrt keinen eigenen
 * "geladen"-Stand: sonst weiss er nichts davon, wenn die Ladefehler-Flaeche oder eine Gruppenaktion den
 * Stand auf einen anderen Filter gestellt hat, und haelt einen laengst ersetzten Filter fuer geladen
 * (Rueckkehr zu "Archivierte" liefe ohne Abruf). Bis zum ersten gelungenen Lauf (`null`) gilt der Filter des
 * Erstladens, den der Haken beim ersten Lauf nach der Anmeldung ermittelt (`erstFilter`). Nur der EIGENE,
 * noch laufende Abruf (`unterwegs`) zaehlt zusaetzlich als kommender Stand: er verhindert, dass dieselbe Wahl
 * waehrend des Laufs einen zweiten Abruf startet.
 *
 * GESCHEITERTER WECHSEL (Fehleranzeige Paket 2, Entscheidung E13): scheitert das Neuladen, zeigt die
 * Liste weiter den alten Stand (der Store meldet das im Fehlerstapel). Die Filterwahl springt dann auf den
 * BESTAETIGTEN Filter zurueck - Auswahl und Adresse, ueber `setzeAnsicht` -, damit Filter und Liste
 * zusammenpassen; nicht auf den zuletzt angeforderten, dessen Lauf von einem neueren abgeloest worden sein
 * kann und nie ankam (Fix-Runde 1, W1). Der gescheiterte Filter gilt nicht als geladen: waehlt der Nutzer
 * ihn erneut, laedt der Haken neu. Kein Zurueckspringen gibt es, wenn der Lauf von einem neueren
 * abgeloest wurde (`"ueberholt"`), wenn der Nutzer waehrenddessen schon weitergewaehlt hat (seine Wahl
 * bleibt), und fuer die Zahlungsrolle (sie laedt unabhaengig von der Adresse; es gibt dort keine Filterwahl
 * zurueckzusetzen).
 */
export function useBoardBestellzustand(
  nutzer: Nutzer | null,
  seite: AktiveSeite,
  states: BoardStateSelection,
  bestandNeuLaden: (states?: BoardStateSelection) => Promise<BestandLaufErgebnis>,
  setzeAnsicht: AnsichtsZugriff["setzeAnsicht"],
  bestaetigterFilter: () => BoardStateSelection | null,
): void {
  /** Der Filter des Erstladens (`ersteZustandswahl`), einmal beim ersten Lauf nach der Anmeldung ermittelt. */
  const erstFilter = useRef<Filter | null>(null);
  /** Der eigene, noch laufende Abruf. */
  const unterwegs = useRef<Filter | null>(null);
  // Ein Lauf endet erst nach dem Rendern, in dem er begann: er liest den jeweils aktuellen Stand.
  const aktuelleStates = useRef(states);
  aktuelleStates.current = states;
  const adresseSchreiben = useRef(setzeAnsicht);
  adresseSchreiben.current = setzeAnsicht;
  const bestaetigtLesen = useRef(bestaetigterFilter);
  bestaetigtLesen.current = bestaetigterFilter;
  useEffect(() => {
    if (!nutzer) return;
    if (erstFilter.current === null) {
      erstFilter.current = filterVon(ersteZustandswahl(nutzer.role));
      return;
    }
    /** Der Filter, den der Store zeigt (oder nach dem eigenen Lauf zeigen wird). */
    const stand = (): Filter => {
      if (unterwegs.current !== null) return unterwegs.current;
      const bestaetigt = bestaetigtLesen.current();
      return bestaetigt !== null ? filterVon(bestaetigt) : erstFilter.current!;
    };
    const istZahlungsrolle = normalisierteRolle(nutzer.role) === "payment_manager";
    if (!istZahlungsrolle && seite !== "board") return;
    const wert: BoardStateSelection = istZahlungsrolle ? ["active", "archived"] : states;
    const schluessel = wert.join(",");
    if (stand().schluessel === schluessel) return;
    // Erst im Timer fortschreiben: Rueckkehr zum geladenen Filter innerhalb der Frist braucht
    // keinen Abruf. Abhaengigkeitswechsel und Abbau verwerfen den ausstehenden Timer.
    const timer = window.setTimeout(() => {
      // Zwischen Planung und Ablauf kann ein anderer Lauf den Stand schon auf diesen Filter gestellt haben.
      if (stand().schluessel === schluessel) return;
      const lauf = filterVon(wert);
      unterwegs.current = lauf;
      void bestandNeuLaden(wert).then((ergebnis) => {
        // Hat der Haken inzwischen einen neueren Abruf gestartet, gehoert der Ausgang diesem.
        if (unterwegs.current !== lauf) return;
        unterwegs.current = null;
        if (ergebnis !== "gescheitert") return;
        // Die Wahl nur zuruecksetzen, solange sie noch die gescheiterte ist - und nie fuer die Zahlungsrolle.
        if (istZahlungsrolle || aktuelleStates.current.join(",") !== schluessel) return;
        // Zurueck auf den BESTAETIGTEN Filter (Fallback: der des Erstladens, solange nichts gelungen ist).
        const ziel = stand();
        adresseSchreiben.current(
          (alt) => ({ board: { ...alt.board, states: ziel.auswahl } }),
          { ersetzen: true },
        );
      });
    }, ZUSTANDSWECHSEL_ENTPRELLUNG_MS);
    return () => window.clearTimeout(timer);
  }, [nutzer, seite, states, bestandNeuLaden]);
}
